import { clsx } from '@alexis/helpers/clsx';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getRealAdPosition } from '@components/flights/FlightSearchResultContentAds/helper';

import { getCurrentSiteState } from '@redux/selectors';

import { AdPositionConfig } from '@wegoTypes/adPositionConfig';

import styles from './FlightSearchResultContentAds.module.scss';

interface FlightSearchResultContentAdsProps {
  bnplToolTipShown: boolean;
  bottomLeaderboardAdsHeight: number;
  leaderboardAdsAndInlineAdsConfig: AdPositionConfig[];
  middleLeaderboardAdsHeight: number;
  nativeBottomAdHeight: number | undefined;
  nativeMiddleAdHeight: number | undefined;
  nativeTopAdHeight: number | undefined;
  topLeaderboardAdsHeight: number;
}

const FlightSearchResultContentAds: React.FC<FlightSearchResultContentAdsProps> = ({
  bnplToolTipShown,
  bottomLeaderboardAdsHeight,
  leaderboardAdsAndInlineAdsConfig,
  middleLeaderboardAdsHeight,
  nativeBottomAdHeight,
  nativeMiddleAdHeight,
  nativeTopAdHeight,
  topLeaderboardAdsHeight,
}) => {
  const { countryCode } = useSelector(getCurrentSiteState);

  const { finalTripViewModelsToShowBasedOnSortType } = useFlightSearchResults();
  const { selectedLegs } = useFlightSearchFilters();

  const hasEgyptCentralBankNotice = countryCode === 'EG';

  const topLeaderboardAdConfig = useMemo(
    () => leaderboardAdsAndInlineAdsConfig.find((config) => config.id === 'top_leaderboard'),
    [leaderboardAdsAndInlineAdsConfig],
  );
  const realTopLeaderboardAdPosition = useMemo(
    () =>
      topLeaderboardAdConfig
        ? getRealAdPosition(topLeaderboardAdConfig, leaderboardAdsAndInlineAdsConfig)
        : 0,
    [leaderboardAdsAndInlineAdsConfig, topLeaderboardAdConfig],
  );

  const middleLeaderboardAdConfig = useMemo(
    () => leaderboardAdsAndInlineAdsConfig.find((config) => config.id === 'middle_leaderboard'),
    [leaderboardAdsAndInlineAdsConfig],
  );
  const realMiddleLeaderboardAdPosition = useMemo(
    () =>
      middleLeaderboardAdConfig
        ? getRealAdPosition(middleLeaderboardAdConfig, leaderboardAdsAndInlineAdsConfig)
        : 0,
    [leaderboardAdsAndInlineAdsConfig, middleLeaderboardAdConfig],
  );

  const bottomLeaderboardAdConfig = useMemo(
    () => leaderboardAdsAndInlineAdsConfig.find((config) => config.id === 'bottom_leaderboard'),
    [leaderboardAdsAndInlineAdsConfig],
  );
  const realBottomLeaderboardAdPosition = useMemo(
    () =>
      bottomLeaderboardAdConfig
        ? getRealAdPosition(bottomLeaderboardAdConfig, leaderboardAdsAndInlineAdsConfig)
        : 0,
    [leaderboardAdsAndInlineAdsConfig, bottomLeaderboardAdConfig],
  );

  const aboveAdSpace = useMemo<number>(() => {
    let heightOfEgyptBankNotice = 0;

    if (hasEgyptCentralBankNotice) {
      // 64 height of div egpytCentralBankNotice
      // 12 margin bottom of egpytCentralBankNotice
      heightOfEgyptBankNotice = 64 + 12;
    }

    // 70 is the height of sort bar
    // 12 is the height of the progress bar
    return 70 + 12 + heightOfEgyptBankNotice;
  }, [hasEgyptCentralBankNotice]);

  const topAdPlaceholder = document.getElementById('placeholder-top_leaderboard')?.offsetTop || 0;
  const middleAdPlaceholder =
    document.getElementById('placeholder-middle_leaderboard')?.offsetTop || 0;
  const bottomAdPlaceholder =
    document.getElementById('placeholder-bottom_leaderboard')?.offsetTop || 0;

  const topLeaderboardTop = useMemo(() => {
    if (topLeaderboardAdConfig && topAdPlaceholder) {
      return aboveAdSpace + topAdPlaceholder;
    }

    return 0;
  }, [aboveAdSpace, topAdPlaceholder, topLeaderboardAdConfig]);

  const middleLeaderboardTop = useMemo(() => {
    if (middleLeaderboardAdConfig && middleAdPlaceholder) {
      return aboveAdSpace + middleAdPlaceholder;
    }

    return 0;
  }, [aboveAdSpace, middleAdPlaceholder, middleLeaderboardAdConfig]);

  const bottomLeaderboardTop = useMemo(() => {
    if (bottomLeaderboardAdConfig && bottomAdPlaceholder) {
      return aboveAdSpace + bottomAdPlaceholder;
    }

    return 0;
  }, [aboveAdSpace, bottomAdPlaceholder, bottomLeaderboardAdConfig]);

  const ads = useMemo(() => {
    return [
      {
        id: 'native_top_ad',
        hidden:
          selectedLegs.length > 0 ||
          !nativeTopAdHeight ||
          !topLeaderboardTop ||
          finalTripViewModelsToShowBasedOnSortType.length < realTopLeaderboardAdPosition,
        top: topLeaderboardTop,
      },
      {
        id: 'native_middle_ad',
        hidden:
          selectedLegs.length > 0 ||
          !middleLeaderboardTop ||
          !nativeMiddleAdHeight ||
          finalTripViewModelsToShowBasedOnSortType.length < realMiddleLeaderboardAdPosition,
        top: middleLeaderboardTop,
      },
      {
        id: 'native_bottom_ad',
        hidden:
          selectedLegs.length > 0 ||
          !bottomLeaderboardTop ||
          !nativeBottomAdHeight ||
          finalTripViewModelsToShowBasedOnSortType.length < realBottomLeaderboardAdPosition,
        top: bottomLeaderboardTop,
      },
      {
        id: 'top_leaderboard',
        hidden:
          selectedLegs.length > 0 ||
          !!nativeTopAdHeight ||
          !topLeaderboardAdsHeight ||
          !topLeaderboardTop ||
          finalTripViewModelsToShowBasedOnSortType.length < realTopLeaderboardAdPosition,
        top: topLeaderboardTop,
      },
      {
        id: 'middle_leaderboard',
        hidden:
          selectedLegs.length > 0 ||
          !!nativeMiddleAdHeight ||
          !middleLeaderboardTop ||
          !middleLeaderboardAdsHeight ||
          finalTripViewModelsToShowBasedOnSortType.length < realMiddleLeaderboardAdPosition,
        top: middleLeaderboardTop,
      },
      {
        id: 'bottom_leaderboard',
        hidden:
          selectedLegs.length > 0 ||
          !!nativeBottomAdHeight ||
          !bottomLeaderboardTop ||
          !bottomLeaderboardAdsHeight ||
          finalTripViewModelsToShowBasedOnSortType.length < realBottomLeaderboardAdPosition,
        top: bottomLeaderboardTop,
      },
    ];
  }, [
    bottomLeaderboardAdsHeight,
    bottomLeaderboardTop,
    finalTripViewModelsToShowBasedOnSortType.length,
    middleLeaderboardAdsHeight,
    middleLeaderboardTop,
    nativeBottomAdHeight,
    nativeMiddleAdHeight,
    nativeTopAdHeight,
    realBottomLeaderboardAdPosition,
    realMiddleLeaderboardAdPosition,
    realTopLeaderboardAdPosition,
    selectedLegs.length,
    topLeaderboardAdsHeight,
    topLeaderboardTop,
  ]);

  return (
    <>
      {ads.map((ad) => (
        <div
          key={ad.id}
          id={ad.id}
          className={clsx(
            styles.leaderboardAdsContainer,
            ad.hidden && styles.hide,
            bnplToolTipShown && styles.tooltipOpen,
          )}
          style={ad.top > 0 ? { top: `${ad.top}px` } : {}}
        ></div>
      ))}
    </>
  );
};

export default FlightSearchResultContentAds;
